import { startOfDay, endOfDay, subDays, formatDistance, subYears, intervalToDuration } from 'date-fns'
import humanizeDuration from 'humanize-duration'

import { TimeRangePreset } from '~/user-stats/types/user-stats.types'

export function formatDuration(ms: number): string {
  return humanizeDuration(ms, { units: ['d', 'h', 'm', 's'], round: true })
}

export function formatDurationCompact(ms: number, locale?: string): string {
  const isNorwegian = locale === 'no' || locale === 'nb'
  const hourUnit = isNorwegian ? 't' : 'h'

  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start: 0, end: Math.max(0, ms) })

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}${hourUnit}`)
  if (minutes > 0) parts.push(`${minutes}m`)
  parts.push(`${seconds}s`)

  return parts.join(' ')
}

export function convertMsToDayHourMinute(ms: number): { days: number; hours: number; minutes: number } {
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  const remainingTimeAfterDays = ms % (24 * 60 * 60 * 1000)
  const hours = Math.floor(remainingTimeAfterDays / (60 * 60 * 1000))
  const remainingTimeAfterHours = ms % (60 * 60 * 1000)
  const minutes = Math.floor(remainingTimeAfterHours / (60 * 1000))
  return { days, hours, minutes }
}

const presetDates = {
  [TimeRangePreset.ALL_TIME]: () => {
    return [undefined, undefined]
  },
  [TimeRangePreset.TODAY]: () => {
    const today = startOfDay(new Date())

    return [today, endOfDay(today)]
  },
  [TimeRangePreset.LAST_7_DAYS]: () => {
    const today = startOfDay(new Date())
    const sevenDaysAgo = subDays(today, 7)

    return [sevenDaysAgo, endOfDay(today)]
  },
  [TimeRangePreset.LAST_30_DAYS]: () => {
    const today = startOfDay(new Date())
    const thirtyDaysAgo = subDays(today, 30)

    return [thirtyDaysAgo, endOfDay(today)]
  },
  [TimeRangePreset.LAST_90_DAYS]: () => {
    const today = startOfDay(new Date())
    const ninetyDaysAgo = subDays(today, 90)

    return [ninetyDaysAgo, endOfDay(today)]
  },
  [TimeRangePreset.LAST_YEAR]: () => {
    const today = startOfDay(new Date())
    const oneYearAgo = subYears(today, 1)

    return [oneYearAgo, endOfDay(today)]
  },
}

export function getDateRangeForPreset(preset: TimeRangePreset): undefined[] | Date[] {
  const dateGeneratorFunc = presetDates[preset]

  if (dateGeneratorFunc == null) {
    throw new Error(`Unknown time range preset: ${preset}`)
  }

  return dateGeneratorFunc()
}

export function formatTimeAgo(date?: Date | string | null): string {
  return date == null ? 'N/A' : formatDistance(new Date(date), new Date(), { addSuffix: true })
}

export function dateCompareFn(a?: string | null, b?: string | null): number {
  const defaultVal = new Date(0).toISOString()
  return (a ?? defaultVal).localeCompare(b ?? defaultVal)
}

export function isValidDateString(date?: string | null): boolean {
  // @ts-ignore
  return !isNaN(new Date(date))
}
